import API_BASE_URL from '../config';
import { getAuthHeaders } from '../utils/authHeaders';
import { fetchWithAuth } from '../utils/fetchWithAuth';

export interface TenantInfo {
  id: string;
  name: string;
  subdomain: string;
  description: string;
}

class TenantService {
  private cachedTenant: TenantInfo | null = null;
  private cachedAtMs: number = 0;
  private inflightRequest: Promise<TenantInfo | null> | null = null;
  private readonly CACHE_TTL_MS: number = 5 * 60 * 1000; // 5 minutes

  private getCacheStorageKey(): string {
    try {
      const userInfoRaw = localStorage.getItem('oidc_user_info');
      if (userInfoRaw) {
        const parsed = JSON.parse(userInfoRaw);
        if (parsed && parsed.userId) {
          return `tenant_info_cache_${parsed.userId}`;
        }
      }
    } catch (_) {
      // ignore
    }
    return 'tenant_info_cache';
  }

  private readCacheFromStorage(): TenantInfo | null {
    try {
      const key = this.getCacheStorageKey();
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { data: TenantInfo; timestamp: number };
      if (parsed && parsed.data && typeof parsed.timestamp === 'number') {
        // honor TTL
        if (Date.now() - parsed.timestamp < this.CACHE_TTL_MS) {
          this.cachedTenant = parsed.data;
          this.cachedAtMs = parsed.timestamp;
          return parsed.data;
        }
      }
    } catch (_) {
      // ignore parse/storage errors
    }
    return null;
  }

  private writeCacheToStorage(data: TenantInfo): void {
    try {
      const key = this.getCacheStorageKey();
      localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (_) {
      // storage might be unavailable; ignore
    }
  }

  /**
   * Clear in-memory and persistent cache (e.g., on logout or tenant change)
   */
  public invalidateCache(): void {
    this.cachedTenant = null;
    this.cachedAtMs = 0;
    this.inflightRequest = null;
    try {
      const key = this.getCacheStorageKey();
      localStorage.removeItem(key);
    } catch (_) {
      // ignore
    }
  }
  /**
   * Get current tenant information
   */
  async getCurrentTenant(forceRefresh: boolean = false): Promise<TenantInfo | null> {
    try {
      // Return in-memory cache if fresh and no force refresh requested
      if (!forceRefresh && this.cachedTenant && (Date.now() - this.cachedAtMs < this.CACHE_TTL_MS)) {
        return this.cachedTenant;
      }

      // Try persistent cache if memory cache is empty or stale
      if (!forceRefresh && (!this.cachedTenant || (Date.now() - this.cachedAtMs >= this.CACHE_TTL_MS))) {
        const stored = this.readCacheFromStorage();
        if (stored) return stored;
      }

      // Coalesce concurrent requests
      if (this.inflightRequest) {
        return this.inflightRequest;
      }

      const headers = getAuthHeaders();
      this.inflightRequest = fetchWithAuth(`${API_BASE_URL}/api/getCurrentTenant`, {
          method: 'GET',
          headers,
        })
        .then(async (response) => {
          if (!response.ok) {
            console.error('Failed to fetch tenant information:', response.statusText);
            return null;
          }
          const tenantData = await response.json();
          // Update caches
          if (tenantData) {
            this.cachedTenant = tenantData;
            this.cachedAtMs = Date.now();
            this.writeCacheToStorage(tenantData);
          }
          return tenantData;
        })
        .finally(() => {
          // clear inflight regardless of outcome to allow future retries
          this.inflightRequest = null;
        });

      return this.inflightRequest;
    } catch (error) {
      console.error('Error fetching tenant information:', error);
      return null;
    }
  }

  /**
   * Get all available tenants
   */
  async getAllTenants(): Promise<TenantInfo[]> {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/getTenants`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.error('Failed to fetch tenants:', response.statusText);
        return [];
      }

      const tenants = await response.json();
      return tenants;
    } catch (error) {
      console.error('Error fetching tenants:', error);
      return [];
    }
  }

  /**
   * Get specific tenant by ID
   */
  async getTenantById(id: string): Promise<TenantInfo | null> {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/getTenant?id=${encodeURIComponent(id)}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.error('Failed to fetch tenant:', response.statusText);
        return null;
      }

      const tenant = await response.json();
      return tenant;
    } catch (error) {
      console.error('Error fetching tenant:', error);
      return null;
    }
  }
}

const tenantService = new TenantService();
export default tenantService; 